/**
 * The Automation screen's data hook.
 *
 * One call: `GET /v1/automation-opportunities/shortlist`. Everything the screen
 * renders comes from it — the ranking, the recommended products, and the pipeline
 * those products form — because the server recomputes the ranking from the
 * tenant's *current* documents on every request and joins the cached product
 * matches onto it.
 *
 * That is what makes the screen dynamic in the way that matters: after a
 * `recompute()` there is no client-side derived state to invalidate, no
 * denormalised copy to refresh and nothing memoised across the reload. `reload()`
 * re-fetches and the new numbers are simply what the screen draws.
 *
 * No location/scanner enrichment here, unlike `useAutomation`: a shortlist row is
 * one document type pooled across every capture dimension, so there is no single
 * location or scanner id to resolve — `spans` reports the counts instead.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/errors';
import type { Shortlist } from '../../api/types';
import { useSession } from '../../auth/session';

export type ShortlistState =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | { status: 'ready'; data: Shortlist; reload: () => void };

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useShortlist(): ShortlistState {
  const { api } = useSession();
  const [state, setState] = useState<ShortlistState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const data = await api.automation.shortlist(signal);
        if (signal.aborted) return;
        setState({ status: 'ready', data, reload });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [api, nonce, reload]);

  return state;
}
